import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login from './components/Login';
import RampExposureWizard from './components/RampExposureWizard';
import ApplicationReview from './components/ApplicationReview';
import leaptranLogo from './assets/Leaptran-logo-color_lg.png';
import fAstLogo from './assets/F-AST Logo.png';
import './App.css';

const THEME_STORAGE_KEY = 'theme-preference';

const getInitialThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'auto';
  }

  const savedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (savedPreference === 'auto' || savedPreference === 'light' || savedPreference === 'dark') {
    return savedPreference;
  }

  return 'auto';
};

function Dashboard({ themePreference, resolvedTheme, onThemeChange }) {
  const { user, logout } = useAuth();
  const nextExplicitTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
  const isAuto = themePreference === 'auto';

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo-lockup" aria-label="Leaptran F-AST">
              <img className="logo logo-leaptran" src={leaptranLogo} alt="Leaptran logo" />
              <span className="logo-divider" aria-hidden="true"></span>
              <img className="logo logo-fast" src={fAstLogo} alt="F-AST logo" />
            </div>
            <span className="tagline">Forecasting-Accelerated Study Tool</span>
          </div>
          <div className="header-right">
            <div className="theme-control" role="group" aria-label="Theme controls">
              <button
                type="button"
                className={`theme-swap-button ${resolvedTheme === 'dark' ? 'is-dark' : 'is-light'}`}
                onClick={() => onThemeChange(nextExplicitTheme)}
                aria-label={`Switch to ${nextExplicitTheme} theme`}
                title={`Switch to ${nextExplicitTheme} theme`}
              >
                <span className="theme-icon-sun" aria-hidden="true"></span>
                <span className="theme-slider" aria-hidden="true">
                  <span className="theme-slider-thumb"></span>
                </span>
                <span className="theme-icon-moon" aria-hidden="true"></span>
              </button>
              <button
                type="button"
                className={`theme-auto-button ${isAuto ? 'is-active' : ''}`}
                onClick={() => onThemeChange(isAuto ? resolvedTheme : 'auto')}
                aria-pressed={isAuto}
                title={isAuto ? 'Auto theme on (click to lock current theme)' : 'Use system theme'}
              >
                Auto
              </button>
            </div>
            <span className="user-email">{user?.email}</span>
            <button className="logout-button" onClick={logout}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <section className="hero">
            <h2>Risk Exposure Analysis</h2>
          </section>

          <RampExposureWizard />

          <section className="info-section">
            <h3>What happens next?</h3>
            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Validation</h4>
                  <p>Our system validates your application for completeness and data accuracy.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Engineering Study</h4>
                  <p>Technical analysis is performed using our forecast models.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Scoring</h4>
                  <p>Receive a quantitative interconnection risk exposure score and assessment report.</p>
                </div>
              </div>
            </div>
          </section>
          <ApplicationReview />
        </div>
      </main>

      <footer className="footer">
        <p>&copy; 2026 Leaptran - Forecasting-Accelerated Study Tool (F-AST)</p>
      </footer>
    </div>
  );
}

function AppContent({ themePreference, resolvedTheme, onThemeChange }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? (
    <Dashboard themePreference={themePreference} resolvedTheme={resolvedTheme} onThemeChange={onThemeChange} />
  ) : (
    <Login />
  );
}

function App() {
  const [themePreference, setThemePreference] = useState(getInitialThemePreference);
  const [resolvedTheme, setResolvedTheme] = useState('light');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      const nextResolvedTheme = themePreference === 'auto'
        ? (mediaQuery.matches ? 'dark' : 'light')
        : themePreference;
      document.documentElement.setAttribute('data-theme', nextResolvedTheme);
      setResolvedTheme(nextResolvedTheme);
    };

    applyTheme();

    if (themePreference !== 'auto') {
      return undefined;
    }

    const handleThemeChange = () => applyTheme();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    }

    mediaQuery.addListener(handleThemeChange);
    return () => mediaQuery.removeListener(handleThemeChange);
  }, [themePreference]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  return (
    <AuthProvider>
      <AppContent
        themePreference={themePreference}
        resolvedTheme={resolvedTheme}
        onThemeChange={setThemePreference}
      />
    </AuthProvider>
  );
}

export default App;
